import { HTMLAttributes, ElementType, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const headingVariants = cva('font-semibold text-foreground', {
  variants: {
    size: {
      xl: 'text-4xl md:text-5xl tracking-tight',
      lg: 'text-3xl md:text-4xl',
      md: 'text-2xl md:text-3xl',
      sm: 'text-xl md:text-2xl',
      xs: 'text-lg md:text-xl',
      base: 'text-base md:text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const textVariants = cva('', {
  variants: {
    variant: {
      body: 'text-foreground',
      muted: 'text-muted-foreground',
      caption: 'text-xs text-muted-foreground',
      error: 'text-destructive',
      success: 'text-success',
      warning: 'text-warning',
      info: 'text-info',
    },
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
  },
  defaultVariants: {
    variant: 'body',
    size: 'base',
    weight: 'normal',
  },
});

// Heading component
interface HeadingProps
  extends HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof headingVariants> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  level?: 1 | 2 | 3 | 4 | 5 | 6; // Semantic level (alternative to 'as')
}

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, size, as, level, ...props }, ref) => {
    // Use 'as' prop if provided, otherwise use 'level' to determine element
    const Comp = as || ((level ? `h${level}` : 'h2') as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6');

    // Default size based on semantic level if size not provided
    const defaultSizeMap: Record<string, 'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'base'> = {
      h1: 'xl',
      h2: 'lg',
      h3: 'md',
      h4: 'sm',
      h5: 'xs',
      h6: 'base',
    };

    const resolvedSize = size || defaultSizeMap[Comp] || 'md';

    return (
      <Comp
        ref={ref}
        className={cn(headingVariants({ size: resolvedSize, className }))}
        {...props}
      />
    );
  }
);
Heading.displayName = 'Heading';

// Text component
type TextElement = HTMLParagraphElement | HTMLSpanElement | HTMLDivElement | HTMLLabelElement;

interface TextProps extends HTMLAttributes<TextElement>, VariantProps<typeof textVariants> {
  as?: 'p' | 'span' | 'div' | 'label';
}

export const Text = forwardRef<TextElement, TextProps>(
  ({ className, variant, size, weight, as: Comp = 'p', ...props }, ref) => {
    const Component = Comp as ElementType;
    return (
      <Component
        ref={ref}
        className={cn(textVariants({ variant, size, weight, className }))}
        {...props}
      />
    );
  }
);
Text.displayName = 'Text';

// Pre-composed variants for common use cases
interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
  children: React.ReactNode;
}

export const Label = forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, children, required, ...props }, ref) => {
    return (
      <label
        ref={ref}
        className={cn('block text-sm font-medium text-foreground mb-1', className)}
        {...props}
      >
        {children}
        {required && <span className="ml-1 text-destructive">*</span>}
      </label>
    );
  }
);
Label.displayName = 'Label';

export const HelperText = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    return (
      <Text ref={ref} size="xs" variant="muted" className={cn('mt-1', className)} {...props} />
    );
  }
);
HelperText.displayName = 'HelperText';

export const ErrorText = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    return (
      <Text ref={ref} size="sm" variant="error" className={cn('mt-1', className)} {...props} />
    );
  }
);
ErrorText.displayName = 'ErrorText';
